/**
 * Vendor-neutral affiliate-link provider interface. See ADR-0064.
 *
 * URL builders are pure functions of their input + a partner marker; they
 * never mutate Booking entities. The default provider is `NoopAffiliateProvider`
 * — returns empty strings / null URLs so UI surfaces can hide themselves
 * when the marker env var is unset.
 */

export interface HotelSearchInput {
  /** Hotel / city / region name shown to the user. */
  destinationName: string;
  destinationLat?: number | undefined;
  destinationLng?: number | undefined;
  /** ISO yyyy-mm-dd. */
  checkIn: string;
  /** ISO yyyy-mm-dd. Falls back to checkIn + 1 day if the caller passes the same value. */
  checkOut: string;
  adults?: number | undefined;
  /** ISO 4217 (USD, EUR, JPY, ...). */
  currency?: string | undefined;
  /** BCP-47 locale (en, en-GB, fr-FR, ...). */
  locale?: string | undefined;
}

export interface FlightSearchInput {
  /** 3-letter IATA airport code, uppercase. */
  originIata: string;
  destinationIata: string;
  /** ISO yyyy-mm-dd. */
  departDate: string;
  /** ISO yyyy-mm-dd; omit for one-way. */
  returnDate?: string | undefined;
  adults?: number | undefined;
  currency?: string | undefined;
  locale?: string | undefined;
}

export interface FlightBrowseInput {
  /** ISO 4217. Forwarded as a hint when supported by the partner. */
  currency?: string | undefined;
  /** BCP-47 locale (en, en-GB, fr-FR, ...). */
  locale?: string | undefined;
}

export interface ToursSearchInput {
  destinationName: string;
  /** ISO yyyy-mm-dd. */
  date?: string | undefined;
  locale?: string | undefined;
  /** Optional category / interest keyword (e.g. 'food', 'history',
   *  'family'). Concatenated into the partner page's search keyword so
   *  themed trips land on a pre-filtered result page. Sourced from
   *  `TripPreferences.interests[0]` at call sites. */
  interest?: string | undefined;
}

export interface CarRentalSearchInput {
  /** Pickup city / airport / address shown to the user. */
  pickupLocation: string;
  /** Drop-off location. Defaults to pickup when omitted. */
  dropoffLocation?: string | undefined;
  /** ISO yyyy-mm-dd. */
  pickupDate: string;
  /** ISO yyyy-mm-dd. Falls back to pickupDate + 1 day if equal or earlier. */
  dropoffDate: string;
  /** 24h HH:mm. Optional — partner defaults if omitted. */
  pickupTime?: string | undefined;
  dropoffTime?: string | undefined;
  currency?: string | undefined;
  locale?: string | undefined;
}

export interface EsimSearchInput {
  /** ISO 3166-1 alpha-2 country code (JP, US, FR, ...) or a region keyword (eu, global). */
  countryCode: string;
  /** Display name used when the partner page accepts a free-form query. */
  countryName?: string | undefined;
  locale?: string | undefined;
}

export interface AirportTransferInput {
  /** Origin display name (airport name, hotel, address). */
  originName: string;
  /** Destination display name. */
  destinationName: string;
  /** ISO yyyy-mm-dd. */
  date: string;
  /** 24h HH:mm. Optional — partner defaults if omitted. */
  time?: string | undefined;
  passengers?: number | undefined;
  currency?: string | undefined;
  locale?: string | undefined;
}

export interface TrainSearchInput {
  /** Departure city / station shown to the user. */
  originName: string;
  /** Arrival city / station shown to the user. */
  destinationName: string;
  /** ISO yyyy-mm-dd. */
  departDate: string;
  /** 24h HH:mm. Optional — partner defaults if omitted. */
  departTime?: string | undefined;
  passengers?: number | undefined;
  /** Hint to the aggregator which sub-mode the user is shopping for.
   *  Omio surfaces a combined search but pre-selects the tab when
   *  this hint is forwarded. Defaults to 'train' on the partner side
   *  when omitted. */
  mode?: 'train' | 'bus' | 'ferry' | undefined;
  currency?: string | undefined;
  locale?: string | undefined;
}

export interface TripWidgetInput {
  destinationName: string;
  /** ISO yyyy-mm-dd. */
  checkIn?: string | undefined;
  /** ISO yyyy-mm-dd. */
  checkOut?: string | undefined;
  locale?: string | undefined;
  currency?: string | undefined;
}

export interface AffiliateWidgetSnippet {
  /** HTML suitable for `<iframe srcDoc>` on web or a native WebView. */
  html: string;
  /** Direct URL for surfaces that prefer `<iframe src>` over inline HTML. */
  url: string;
}

export interface AffiliateProvider {
  readonly id: string;
  /** Build a hotel search deep-link with attribution. */
  buildHotelSearchUrl(input: HotelSearchInput): string;
  /** Build a flight search deep-link. Returns null when required IATA codes are missing. */
  buildFlightSearchUrl(input: FlightSearchInput): string | null;
  /** Build a marker-tagged flight browse URL (partner search homepage)
   *  for surfaces where IATA codes aren't known. Optional — providers
   *  that can't attribute a no-input click should omit this. */
  buildFlightBrowseUrl?: (input: FlightBrowseInput) => string;
  /** Optional: tours / activities deep-link. */
  buildToursSearchUrl?: (input: ToursSearchInput) => string;
  /** Optional: car rental deep-link. */
  buildCarRentalSearchUrl?: (input: CarRentalSearchInput) => string;
  /** Optional: eSIM deep-link (data plan for a destination country). */
  buildEsimSearchUrl?: (input: EsimSearchInput) => string;
  /** Optional: airport transfer deep-link. */
  buildAirportTransferUrl?: (input: AirportTransferInput) => string;
  /** Optional: intercity rail / bus / ferry deep-link. One builder
   *  covers all three modes because TravelPayouts aggregates them
   *  under one partner (Omio); the `mode` hint preselects the tab. */
  buildTrainSearchUrl?: (input: TrainSearchInput) => string;
  /** Optional: in-app search widget snippet for the trip detail screen. */
  buildTripWidget?: (input: TripWidgetInput) => AffiliateWidgetSnippet;
}

/**
 * Returns empty strings / null URLs. The default when no marker env is set.
 * UI surfaces should branch on `provider.id === 'noop'` and render nothing.
 */
export class NoopAffiliateProvider implements AffiliateProvider {
  readonly id = 'noop';
  buildHotelSearchUrl(_input: HotelSearchInput): string {
    return '';
  }
  buildFlightSearchUrl(_input: FlightSearchInput): string | null {
    return null;
  }
}

export interface AffiliateEnv {
  /** TravelPayouts marker / partner identifier. */
  travelpayoutsMarker?: string | undefined;
  /** Optional TravelPayouts trs (sub-ID for white-label widgets). */
  travelpayoutsTrs?: string | undefined;
  /** Optional white-label widget host override (defaults to tp.media). */
  travelpayoutsHost?: string | undefined;
}
